import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, ArrowLeft, Save, Trash2, Users, FilePlus2, Download, FileType, Wand2, X, Pencil, Send, Clock, Layers, Paperclip, Upload, KeyRound, AlertTriangle, FileUp, HandCoins, Receipt, CalendarClock, FileText, Bell, Handshake, Copy } from "lucide-react";
import { api, API, apiError } from "@/lib/api";

const EMPTY_CLIENT = { name: "", contact_person: "", email: "", phone: "", address: "", default_court: "", client_reference: "", gst_number: "", notes: "" };
const EMPTY_MATTER = { party_name: "", party_address: "", party_email: "", amount: "", settlement_amount: "", writeoff_amount: "", client_ref: "", agreement_desc: "", case_number: "", case_year: "", court: "", status: "Open", notes: "" };
const MATTER_STATUSES = ["Open", "In Progress", "Recovered", "Closed"];
const money = (v) => `$${Number(v || 0).toLocaleString("en-NZ", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const MATTER_STATUS_STYLE = {
  "Open": "bg-[#F5F3EC] text-[#58635C] border-[#E5E1D8]",
  "In Progress": "bg-[#FBF2DE] text-[#8A6D2F] border-[#E8D9B0]",
  "Recovered": "bg-[#E7F0E9] text-[#3F6B4E] border-[#C6DDCC]",
  "Closed": "bg-[#0A0D0B] text-white border-[#0A0D0B]",
};

function staleDays(m) {
  const status = m.status || "Open";
  if (status === "Recovered" || status === "Closed") return 0;
  const since = m.status_since || m.created_at;
  if (!since) return 0;
  return Math.floor((Date.now() - new Date(since).getTime()) / 86400000);
}

export default function ClientsPanel() {
  const [clients, setClients] = useState(null);
  const [courts, setCourts] = useState([]);
  const [selId, setSelId] = useState(null);
  const [adding, setAdding] = useState(false);
  const [attention, setAttention] = useState(null);

  const load = () => api.get("/clients").then((r) => setClients(r.data.clients));
  useEffect(() => {
    load();
    api.get("/courts").then((r) => setCourts(r.data.courts));
    api.get("/matters/attention?days=14").then((r) => setAttention(r.data)).catch(() => {});
  }, []);

  if (selId) return <ClientDetail clientId={selId} courts={courts} onBack={() => { setSelId(null); load(); api.get("/matters/attention?days=14").then((r) => setAttention(r.data)).catch(() => {}); }} />;

  return (
    <div>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl md:text-4xl text-[#0A0D0B]">Clients</h1>
          <p className="mt-2 text-[#58635C]">Client files, the parties you're recovering from, and one-click letter generation.</p>
        </div>
        <button onClick={() => setAdding(true)} data-testid="add-client-btn"
          className="inline-flex items-center gap-2 bg-[#0A0D0B] text-white px-5 py-3 text-sm font-semibold uppercase tracking-[0.1em] hover:bg-[#233027] transition-colors">
          <Plus size={16} /> New client
        </button>
      </div>

      {attention && attention.count > 0 && (
        <div data-testid="attention-banner" className="mt-6 flex items-start gap-3 bg-[#FBF2DE] border border-[#E8D9B0] px-5 py-4">
          <AlertTriangle size={18} className="text-[#8A6D2F] mt-0.5 shrink-0" />
          <div>
            <div className="font-medium text-[#6E5620]">{attention.count} matter{attention.count === 1 ? "" : "s"} need attention</div>
            <div className="text-sm text-[#8A6D2F]">Open or in-progress with no status change for over {attention.days} days: {attention.matters.slice(0, 4).map((m) => `${m.party_name} (${m.days}d)`).join(", ")}{attention.matters.length > 4 ? "…" : ""}</div>
          </div>
        </div>
      )}

      {!clients ? <Loader2 className="animate-spin text-[#B89874] mt-8" /> : clients.length === 0 ? (
        <div className="mt-8 border border-dashed border-[#E5E1D8] p-12 text-center text-[#58635C]">No clients yet. Add your first client to begin.</div>
      ) : (
        <div className="mt-8 grid sm:grid-cols-2 gap-4">
          {clients.map((c) => (
            <button key={c.id} data-testid={`client-card-${c.id}`} onClick={() => setSelId(c.id)}
              className="text-left bg-white border border-[#E5E1D8] p-5 hover:border-[#B89874] transition-colors">
              <div className="flex items-center gap-2 text-[#B89874]"><Users size={15} /><span className="text-[11px] uppercase tracking-wide">{c.matter_count} matter{c.matter_count === 1 ? "" : "s"}</span>
                {c.overdue_count > 0 && <span data-testid={`client-overdue-${c.id}`} className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide px-2 py-0.5 border border-red-300 bg-red-50 text-red-700 font-semibold ml-1"><AlertTriangle size={10} /> {c.overdue_count} × 90+</span>}
              </div>
              <div className="font-display text-xl text-[#0A0D0B] mt-2">{c.name}</div>
              <div className="text-sm text-[#58635C] mt-1">{c.contact_person || "—"} {c.email && `· ${c.email}`}</div>
              {c.default_court && <div className="text-xs text-[#B89874] mt-2">{c.default_court}</div>}
            </button>
          ))}
        </div>
      )}

      {adding && <ClientForm courts={courts} initial={EMPTY_CLIENT}
        onClose={() => setAdding(false)}
        onSave={async (data) => { await api.post("/clients", data); toast.success("Client added."); setAdding(false); load(); }} />}
    </div>
  );
}

function ClientDetail({ clientId, courts, onBack }) {
  const [client, setClient] = useState(null);
  const [editing, setEditing] = useState(false);
  const [matterForm, setMatterForm] = useState(null);
  const [gen, setGen] = useState(null);
  const [generated, setGenerated] = useState([]);
  const [bulk, setBulk] = useState(false);
  const [emailGen, setEmailGen] = useState(null);
  const [timelineFor, setTimelineFor] = useState(null);
  const [filesFor, setFilesFor] = useState(null);
  const [paymentsFor, setPaymentsFor] = useState(null);
  const [portalOpen, setPortalOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [settle, setSettle] = useState(null);

  const load = () => api.get(`/clients/${clientId}`).then((r) => setClient(r.data));
  const loadGen = () => api.get(`/generated?client_id=${clientId}`).then((r) => setGenerated(r.data.generated));
  useEffect(() => { load(); loadGen(); }, [clientId]);

  if (!client) return <Loader2 className="animate-spin text-[#B89874]" />;

  const setStatus = async (m, status) => {
    await api.patch(`/matters/${m.id}/status`, { status });
    setClient((c) => ({ ...c, matters: c.matters.map((x) => (x.id === m.id ? { ...x, status } : x)) }));
    toast.success(`Marked ${status}.`);
  };

  const delMatter = (m) => setConfirm({
    title: "Delete matter",
    message: `Delete the matter for “${m.party_name}”? This also removes its payments, plan, files and timeline. This cannot be undone.`,
    confirmLabel: "Delete matter",
    onConfirm: async () => { await api.delete(`/matters/${m.id}`); toast.success("Matter deleted."); load(); },
  });
  const delClient = () => setConfirm({
    title: "Delete client",
    message: `Delete “${client.name}” and all ${client.matters.length} of its matters, payments and files? This cannot be undone.`,
    confirmLabel: "Delete client",
    onConfirm: async () => { await api.delete(`/clients/${clientId}`); toast.success("Client deleted."); onBack(); },
  });

  return (
    <div>
      <button onClick={onBack} className="inline-flex items-center gap-2 text-sm text-[#58635C] hover:text-[#0A0D0B] mb-6"><ArrowLeft size={15} /> All clients</button>
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl md:text-4xl text-[#0A0D0B]">{client.name}</h1>
          <p className="mt-2 text-[#58635C]">{client.contact_person} {client.email && `· ${client.email}`} {client.phone && `· ${client.phone}`}</p>
          {client.default_court && <p className="text-sm text-[#B89874] mt-1">Default court: {client.default_court}{client.client_reference && ` · Ref ${client.client_reference}`}</p>}
        </div>
        <div className="flex gap-2">
          <button onClick={() => setPortalOpen(true)} data-testid="portal-access-btn" className="inline-flex items-center gap-2 border border-[#E5E1D8] text-[#58635C] px-4 py-2.5 text-sm font-semibold hover:border-[#0A0D0B] hover:text-[#0A0D0B] transition-colors"><KeyRound size={14} /> Portal {client.portal_enabled ? "✓" : ""}</button>
          <button onClick={() => setEditing(true)} data-testid="edit-client-btn" className="inline-flex items-center gap-2 border border-[#E5E1D8] text-[#58635C] px-4 py-2.5 text-sm font-semibold hover:border-[#0A0D0B] hover:text-[#0A0D0B] transition-colors"><Pencil size={14} /> Edit</button>
          <button onClick={delClient} data-testid="delete-client-btn" className="inline-flex items-center gap-2 border border-[#E5E1D8] text-[#58635C] px-4 py-2.5 text-sm font-semibold hover:border-red-500 hover:text-red-600 transition-colors"><Trash2 size={14} /></button>
        </div>
      </div>
      {client.notes && <p className="mt-3 text-sm text-[#58635C] bg-[#FBFAF5] border border-[#F0EDE4] p-3">{client.notes}</p>}

      <div className="mt-8 flex items-center justify-between flex-wrap gap-2">
        <h2 className="font-display text-2xl text-[#0A0D0B]">Matters / parties</h2>
        <div className="flex gap-2">
          <button onClick={() => setImporting(true)} data-testid="import-csv-btn"
            className="inline-flex items-center gap-2 border border-[#E5E1D8] text-[#58635C] px-4 py-2.5 text-sm font-semibold hover:border-[#0A0D0B] hover:text-[#0A0D0B] transition-colors"><FileUp size={15} /> Import CSV</button>
          {client.matters.length > 1 && (
            <button onClick={() => setBulk(true)} data-testid="bulk-generate-btn"
              className="inline-flex items-center gap-2 border border-[#0A0D0B] text-[#0A0D0B] px-4 py-2.5 text-sm font-semibold hover:bg-[#0A0D0B] hover:text-white transition-colors"><Layers size={15} /> Bulk letters</button>
          )}
          <button onClick={() => setMatterForm(EMPTY_MATTER)} data-testid="add-matter-btn"
            className="inline-flex items-center gap-2 bg-[#0A0D0B] text-white px-4 py-2.5 text-sm font-semibold hover:bg-[#233027] transition-colors"><Plus size={15} /> Add matter</button>
        </div>
      </div>

      {client.matters.length === 0 ? (
        <div className="mt-4 border border-dashed border-[#E5E1D8] p-10 text-center text-[#58635C]">No matters yet. Add the party you're recovering from, then generate a letter in one click.</div>
      ) : (
        <div className="mt-4 space-y-3">
          {client.matters.map((m) => (
            <div key={m.id} data-testid={`matter-${m.id}`} className="bg-white border border-[#E5E1D8] p-5">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-[#0A0D0B]">{m.party_name}</span>
                    <span data-testid={`matter-status-${m.id}`} className={`text-[10px] uppercase tracking-wide px-2 py-0.5 border ${MATTER_STATUS_STYLE[m.status || "Open"]}`}>{m.status || "Open"}</span>
                    {staleDays(m) >= 14 && <span data-testid={`stale-${m.id}`} className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide px-2 py-0.5 border border-[#E8D9B0] bg-[#FBF2DE] text-[#8A6D2F]"><AlertTriangle size={10} /> {staleDays(m)}d</span>}
                    {m.age_days > 90 && m.outstanding > 0 && <span data-testid={`overdue-${m.id}`} className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide px-2 py-0.5 border border-red-300 bg-red-50 text-red-700 font-semibold"><AlertTriangle size={10} /> 90+ overdue · {m.age_days}d</span>}
                    {m.plan_missed > 0 && <span data-testid={`plan-missed-${m.id}`} className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide px-2 py-0.5 border border-red-300 bg-red-50 text-red-700 font-semibold"><AlertTriangle size={10} /> {m.plan_missed} missed</span>}
                  </div>
                  <div className="text-sm text-[#58635C]">{m.party_address}{m.party_email && ` · ${m.party_email}`}</div>
                  <div className="text-xs text-[#B89874] font-mono-gg mt-1">
                    {m.amount && <span className="mr-3">Amount: {m.amount}</span>}
                    {m.client_ref && <span className="mr-3">Ref: {m.client_ref}</span>}
                    {m.case_number && <span>Case: {m.case_number}</span>}
                  </div>
                  {m.original_amount > 0 && (
                    <div data-testid={`matter-balance-${m.id}`} className="mt-1.5 text-xs font-mono-gg flex flex-wrap gap-x-3 gap-y-0.5">
                      {m.settlement > 0 && (
                        <>
                          <span className="text-[#58635C]">Original {money(m.original_amount)}</span>
                          <span className="text-[#8A6D2F]">Settled at {money(m.settlement)}</span>
                          {m.writeoff > 0 && <span className="text-[#58635C]">Written off {money(m.writeoff)}</span>}
                        </>
                      )}
                      <span className="text-[#3F6B4E]">Paid {money(m.paid)}</span>
                      <span className={m.outstanding > 0 ? "text-[#8A6D2F] font-semibold" : "text-[#3F6B4E] font-semibold"}>
                        {m.outstanding > 0 ? `Outstanding ${money(m.outstanding)}` : "Fully paid"}
                      </span>
                    </div>
                  )}
                  {m.settlement_status && (
                    <div className="mt-1.5 flex items-center gap-2 flex-wrap" data-testid={`settlement-status-${m.id}`}>
                      <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wide px-2 py-0.5 border font-semibold ${m.settlement_status === "Accepted" ? "border-[#C6DDCC] bg-[#E7F0E9] text-[#3F6B4E]" : "border-[#E8D9B0] bg-[#FBF2DE] text-[#8A6D2F]"}`}>
                        <Handshake size={10} /> Settlement {m.settlement_status}{m.settlement_accepted_name ? ` · ${m.settlement_accepted_name}` : ""}
                      </span>
                      {m.settlement_token && (
                        <button onClick={() => { navigator.clipboard?.writeText(`${window.location.origin}/settle/${m.settlement_token}`); toast.success("Acceptance link copied."); }}
                          data-testid={`settlement-copy-${m.id}`} className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide text-[#58635C] hover:text-[#0A0D0B] transition-colors">
                          <Copy size={11} /> Copy debtor link
                        </button>
                      )}
                    </div>
                  )}
                </div>
                <div className="flex flex-wrap gap-2 items-center">
                  <select value={m.status || "Open"} onChange={(e) => setStatus(m, e.target.value)} data-testid={`matter-status-select-${m.id}`}
                    className="border border-[#E5E1D8] px-2.5 py-2 text-sm bg-white focus:border-[#B89874] outline-none">
                    {MATTER_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                  <button onClick={() => setGen({ matter: m })} data-testid={`generate-btn-${m.id}`}
                    className="inline-flex items-center gap-2 bg-[#B89874] text-[#0A0D0B] px-4 py-2.5 text-sm font-semibold hover:bg-[#C9A780] transition-colors"><Wand2 size={15} /> Generate</button>
                  <button onClick={() => setSettle({ matter: m })} data-testid={`settle-btn-${m.id}`} title="Full & final settlement"
                    className="inline-flex items-center gap-2 border border-[#8A6D2F] text-[#8A6D2F] px-4 py-2.5 text-sm font-semibold hover:bg-[#8A6D2F] hover:text-white transition-colors"><Handshake size={15} /> Settle</button>
                  <a href={`${API}/matters/${m.id}/statement`} target="_blank" rel="noreferrer" data-testid={`statement-btn-${m.id}`} title="Statement of account"
                    className="p-2.5 border border-[#E5E1D8] text-[#58635C] hover:text-[#0A0D0B] hover:border-[#0A0D0B] transition-colors"><FileText size={14} /></a>
                  <button onClick={() => { setPaymentsFor(paymentsFor === m.id ? null : m.id); setFilesFor(null); setTimelineFor(null); }} data-testid={`payments-btn-${m.id}`} className="p-2.5 border border-[#E5E1D8] text-[#58635C] hover:text-[#3F6B4E] hover:border-[#3F6B4E] transition-colors"><HandCoins size={14} /></button>
                  <button onClick={() => { setFilesFor(filesFor === m.id ? null : m.id); setTimelineFor(null); setPaymentsFor(null); }} data-testid={`files-btn-${m.id}`} className="p-2.5 border border-[#E5E1D8] text-[#58635C] hover:text-[#0A0D0B] hover:border-[#0A0D0B] transition-colors"><Paperclip size={14} /></button>
                  <button onClick={() => { setTimelineFor(timelineFor === m.id ? null : m.id); setFilesFor(null); setPaymentsFor(null); }} data-testid={`timeline-btn-${m.id}`} className="p-2.5 border border-[#E5E1D8] text-[#58635C] hover:text-[#0A0D0B] hover:border-[#0A0D0B] transition-colors"><Clock size={14} /></button>
                  <button onClick={() => setMatterForm({ ...m })} data-testid={`edit-matter-${m.id}`} className="p-2.5 border border-[#E5E1D8] text-[#58635C] hover:text-[#0A0D0B] hover:border-[#0A0D0B] transition-colors"><Pencil size={14} /></button>
                  <button onClick={() => delMatter(m)} data-testid={`delete-matter-${m.id}`} className="p-2.5 border border-[#E5E1D8] text-[#58635C] hover:text-red-600 hover:border-red-500 transition-colors"><Trash2 size={14} /></button>
                </div>
              </div>
              {timelineFor === m.id && <MatterTimeline matterId={m.id} />}
              {filesFor === m.id && <MatterFiles matterId={m.id} />}
              {paymentsFor === m.id && <MatterPayments matterId={m.id} onChange={load} />}
            </div>
          ))}
        </div>
      )}

      <div className="mt-10">
        <h2 className="font-display text-2xl text-[#0A0D0B]">Generated documents</h2>
        {generated.length === 0 ? (
          <p className="mt-3 text-sm text-[#58635C]">Documents you generate for this client will appear here.</p>
        ) : (
          <div className="mt-4 space-y-2">
            {generated.map((g) => (
              <div key={g.id} data-testid={`generated-${g.id}`} className="flex flex-wrap items-center justify-between gap-3 bg-white border border-[#E5E1D8] px-5 py-3">
                <div>
                  <div className="text-sm text-[#0A0D0B]">{g.title} <span className="text-[#58635C]">· {g.party_name}</span></div>
                  <div className="text-xs text-[#B89874] font-mono-gg">Ref {g.reference} · {new Date(g.created_at).toLocaleString("en-NZ")}{g.emailed_at && ` · emailed ${g.emailed_to}`}</div>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => setEmailGen(g)} data-testid={`email-generated-${g.id}`} className="inline-flex items-center gap-2 bg-[#0A0D0B] text-white px-4 py-2 text-sm font-semibold hover:bg-[#233027] transition-colors"><Send size={14} /> Email</button>
                  <a href={`${API}/generated/${g.id}/pdf`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 border border-[#0A0D0B] text-[#0A0D0B] px-4 py-2 text-sm font-semibold hover:bg-[#0A0D0B] hover:text-white transition-colors"><Download size={14} /> PDF</a>
                  <a href={`${API}/generated/${g.id}/docx`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 border border-[#E5E1D8] text-[#58635C] px-4 py-2 text-sm font-semibold hover:border-[#0A0D0B] hover:text-[#0A0D0B] transition-colors"><FileType size={14} /> Word</a>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {editing && <ClientForm courts={courts} initial={client}
        onClose={() => setEditing(false)}
        onSave={async (data) => { await api.put(`/clients/${clientId}`, data); toast.success("Client updated."); setEditing(false); load(); }} />}
      {matterForm && <MatterForm courts={courts} initial={matterForm}
        onClose={() => setMatterForm(null)}
        onSave={async (data) => {
          if (matterForm.id) { await api.put(`/matters/${matterForm.id}`, data); toast.success("Matter updated."); }
          else { await api.post(`/clients/${clientId}/matters`, data); toast.success("Matter added."); }
          setMatterForm(null); load();
        }} />}
      {gen && <GenerateModal clientId={clientId} matter={gen.matter} onClose={() => setGen(null)} onGenerated={() => { loadGen(); load(); }} />}
      {settle && <SettleModal matter={settle.matter} onClose={() => setSettle(null)} onDone={() => { setSettle(null); loadGen(); load(); }} onRefresh={load} />}
      {bulk && <BulkModal clientId={clientId} matters={client.matters} onClose={() => setBulk(false)} onDone={() => { loadGen(); load(); }} />}
      {emailGen && <EmailModal gen={emailGen} onClose={() => setEmailGen(null)} onSent={() => { setEmailGen(null); loadGen(); load(); }} />}
      {portalOpen && <PortalModal client={client} onClose={() => setPortalOpen(false)} onSaved={() => { setPortalOpen(false); load(); }} />}
      {importing && <ImportModal clientId={clientId} onClose={() => setImporting(false)} onImported={() => load()} />}
      {confirm && <ConfirmDialog {...confirm} onClose={() => setConfirm(null)} />}
    </div>
  );
}

function ConfirmDialog({ title, message, confirmLabel = "Confirm", onConfirm, onClose }) {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try { await onConfirm(); onClose(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); setBusy(false); }
  };
  return (
    <Modal testid="confirm-dialog" title={title} subtitle="Please confirm" onClose={onClose}
      footer={<>
        <button onClick={onClose} data-testid="confirm-cancel" className="px-5 py-3 text-sm font-semibold text-[#58635C] hover:text-[#0A0D0B]">Cancel</button>
        <button onClick={run} disabled={busy} data-testid="confirm-accept"
          className="inline-flex items-center gap-2 bg-red-600 text-white px-6 py-3 text-sm font-semibold uppercase tracking-[0.1em] hover:bg-red-700 transition-colors disabled:opacity-60">
          {busy ? <Loader2 size={15} className="animate-spin" /> : <Trash2 size={15} />} {confirmLabel}
        </button>
      </>}>
      <p className="text-sm text-[#233027] leading-relaxed">{message}</p>
    </Modal>
  );
}

function MatterTimeline({ matterId }) {
  const [acts, setActs] = useState(null);
  const [note, setNote] = useState("");
  const [big, setBig] = useState(false);
  const [busy, setBusy] = useState(false);
  const [delId, setDelId] = useState(null);
  const [editId, setEditId] = useState(null);
  const [editText, setEditText] = useState("");
  const load = () => api.get(`/matters/${matterId}/activity`).then((r) => setActs(r.data.activity));
  useEffect(() => { load(); }, [matterId]);

  const addNote = async () => {
    if (!note.trim()) return;
    setBusy(true);
    try {
      await api.post(`/matters/${matterId}/note`, { message: note });
      setNote(""); setBig(false); toast.success("Note added."); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const delNote = async (id) => {
    setDelId(id);
    try {
      await api.delete(`/matters/${matterId}/note/${id}`);
      toast.success("Note deleted."); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setDelId(null); }
  };

  const startEdit = (a) => { setEditId(a.id); setEditText(a.detail); };
  const cancelEdit = () => { setEditId(null); setEditText(""); };
  const saveEdit = async (id) => {
    if (!editText.trim()) return;
    setBusy(true);
    try {
      await api.put(`/matters/${matterId}/note/${id}`, { message: editText });
      toast.success("Note updated."); cancelEdit(); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  return (
    <div className="mt-4 border-t border-[#F0EDE4] pt-3" data-testid={`timeline-${matterId}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-[11px] uppercase tracking-[0.16em] text-[#58635C]">Activity</div>
        <button onClick={() => setBig((v) => !v)} data-testid={`note-toggle-big-${matterId}`}
          className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-[#3F6B4E] hover:text-[#0A0D0B] transition-colors">
          <Layers size={12} /> {big ? "Quick note" : "Large note"}
        </button>
      </div>
      {big ? (
        <div className="mb-3">
          <textarea value={note} onChange={(e) => setNote(e.target.value)} data-testid={`note-textarea-${matterId}`}
            rows={6} placeholder="Write a detailed note — meeting summary, call log, instructions…"
            className="w-full border border-[#E5E1D8] px-3 py-2 text-sm focus:border-[#B89874] outline-none resize-y leading-relaxed" />
          <div className="flex justify-end mt-2">
            <button onClick={addNote} disabled={busy || !note.trim()} data-testid={`note-add-big-${matterId}`}
              className="inline-flex items-center gap-1.5 text-xs font-semibold border border-[#0A0D0B] text-[#0A0D0B] px-4 py-2 hover:bg-[#0A0D0B] hover:text-white transition-colors disabled:opacity-60">
              {busy ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />} Save note
            </button>
          </div>
        </div>
      ) : (
        <div className="flex gap-2 mb-3">
          <input value={note} onChange={(e) => setNote(e.target.value)} data-testid={`note-input-${matterId}`}
            onKeyDown={(e) => e.key === "Enter" && addNote()} placeholder="Add a quick note (e.g. called debtor)…"
            className="flex-1 border border-[#E5E1D8] px-3 py-2 text-sm focus:border-[#B89874] outline-none" />
          <button onClick={addNote} disabled={busy} data-testid={`note-add-${matterId}`}
            className="inline-flex items-center gap-1.5 text-xs font-semibold border border-[#0A0D0B] text-[#0A0D0B] px-3 py-2 hover:bg-[#0A0D0B] hover:text-white transition-colors disabled:opacity-60">
            {busy ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />} Note
          </button>
        </div>
      )}
      {!acts ? <Loader2 size={15} className="animate-spin text-[#B89874]" /> : acts.length === 0 ? (
        <p className="text-sm text-[#58635C]">No activity yet.</p>
      ) : (
        <ul className="space-y-1.5">
          {acts.map((a) => (
            editId === a.id ? (
              <li key={a.id} className="text-sm">
                <textarea value={editText} onChange={(e) => setEditText(e.target.value)} data-testid={`note-edit-textarea-${a.id}`}
                  rows={Math.min(8, Math.max(2, editText.split("\n").length))}
                  className="w-full border border-[#B89874] px-3 py-2 text-sm focus:border-[#0A0D0B] outline-none resize-y leading-relaxed" />
                <div className="flex justify-end gap-2 mt-1.5">
                  <button onClick={cancelEdit} data-testid={`note-edit-cancel-${a.id}`}
                    className="inline-flex items-center gap-1.5 text-xs font-semibold border border-[#E5E1D8] text-[#58635C] px-3 py-1.5 hover:border-[#0A0D0B] hover:text-[#0A0D0B] transition-colors">
                    <X size={13} /> Cancel
                  </button>
                  <button onClick={() => saveEdit(a.id)} disabled={busy || !editText.trim()} data-testid={`note-edit-save-${a.id}`}
                    className="inline-flex items-center gap-1.5 text-xs font-semibold border border-[#0A0D0B] text-[#0A0D0B] px-3 py-1.5 hover:bg-[#0A0D0B] hover:text-white transition-colors disabled:opacity-60">
                    {busy ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Save
                  </button>
                </div>
              </li>
            ) : (
            <li key={a.id} className="group flex items-start gap-2 text-sm">
              <span className={`mt-1.5 w-1.5 h-1.5 rounded-full shrink-0 ${a.kind === "note" ? "bg-[#3F6B4E]" : "bg-[#B89874]"}`} />
              <span className="text-[#233027] whitespace-pre-wrap break-words">{a.kind === "note" && <span className="text-[10px] uppercase tracking-wide text-[#3F6B4E] mr-1.5">Note</span>}{a.detail}{a.edited_at && <span className="text-[10px] text-[#B89874] italic ml-1.5">(edited)</span>}</span>
              <span className="text-[11px] text-[#B89874] font-mono-gg ml-auto shrink-0">{new Date(a.created_at).toLocaleString("en-NZ")}</span>
              {a.kind === "note" && (
                <span className="flex items-center gap-1.5 shrink-0 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
                  <button onClick={() => startEdit(a)} data-testid={`note-edit-${a.id}`}
                    title="Edit note"
                    className="text-[#B89874] hover:text-[#0A0D0B] transition-colors">
                    <Pencil size={13} />
                  </button>
                  <button onClick={() => delNote(a.id)} disabled={delId === a.id} data-testid={`note-delete-${a.id}`}
                    title="Delete note"
                    className="text-[#B89874] hover:text-[#B0403A] transition-colors disabled:opacity-50">
                    {delId === a.id ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />}
                  </button>
                </span>
              )}
            </li>
            )
          ))}
        </ul>
      )}
    </div>
  );
}

function Field({ label, value, onChange, testid, type = "text", full }) {
  return (
    <div className={`flex flex-col gap-1.5 ${full ? "sm:col-span-2" : ""}`}>
      <label className="text-[11px] uppercase tracking-[0.16em] text-[#58635C]">{label}</label>
      <input data-testid={testid} type={type} value={value || ""} onChange={(e) => onChange(e.target.value)}
        className="border border-[#E5E1D8] px-3.5 py-2.5 focus:border-[#B89874] outline-none" />
    </div>
  );
}

function Modal({ title, subtitle, onClose, children, footer, testid }) {
  return (
    <div className="fixed inset-0 z-[100] flex items-start md:items-center justify-center p-4 bg-[#0A0D0B]/70 backdrop-blur-sm overflow-y-auto">
      <div className="bg-white w-full max-w-2xl border border-[#E5E1D8] my-8" data-testid={testid}>
        <div className="flex items-center justify-between px-6 py-5 border-b border-[#E5E1D8]">
          <div>
            <div className="text-[10px] uppercase tracking-[0.24em] text-[#B89874]">{subtitle}</div>
            <h3 className="font-display text-2xl text-[#0A0D0B]">{title}</h3>
          </div>
          <button onClick={onClose} className="p-2 text-[#58635C] hover:text-[#0A0D0B]"><X size={20} /></button>
        </div>
        <div className="px-6 py-6">{children}</div>
        <div className="px-6 py-5 border-t border-[#E5E1D8] flex justify-end gap-3">{footer}</div>
      </div>
    </div>
  );
}

function ClientForm({ initial, courts, onClose, onSave }) {
  const [f, setF] = useState({ ...EMPTY_CLIENT, ...initial });
  const [busy, setBusy] = useState(false);
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));
  const submit = async () => {
    if (!f.name.trim()) return toast.error("Client name is required");
    setBusy(true);
    try { await onSave(f); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <Modal testid="client-form" title={initial.id ? "Edit client" : "New client"} subtitle="Client file" onClose={onClose}
      footer={<>
        <button onClick={onClose} className="px-5 py-3 text-sm font-semibold text-[#58635C] hover:text-[#0A0D0B]">Cancel</button>
        <button onClick={submit} disabled={busy} data-testid="client-save" className="inline-flex items-center gap-2 bg-[#0A0D0B] text-white px-6 py-3 text-sm font-semibold uppercase tracking-[0.1em] hover:bg-[#233027] transition-colors disabled:opacity-60">{busy ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Save</button>
      </>}>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Business / legal name" value={f.name} onChange={set("name")} testid="client-name" full />
        <Field label="Contact person" value={f.contact_person} onChange={set("contact_person")} testid="client-contact" />
        <Field label="Email" value={f.email} onChange={set("email")} testid="client-email" type="email" />
        <Field label="Phone" value={f.phone} onChange={set("phone")} testid="client-phone" />
        <Field label="Client reference" value={f.client_reference} onChange={set("client_reference")} testid="client-ref" />
        <Field label="Postal address" value={f.address} onChange={set("address")} testid="client-address" full />
        <div className="flex flex-col gap-1.5">
          <label className="text-[11px] uppercase tracking-[0.16em] text-[#58635C]">Default court / registry</label>
          <select data-testid="client-court" value={f.default_court} onChange={(e) => set("default_court")(e.target.value)}
            className="border border-[#E5E1D8] px-3.5 py-2.5 focus:border-[#B89874] outline-none bg-white">
            <option value="">— none —</option>
            {courts.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <Field label="GST number" value={f.gst_number} onChange={set("gst_number")} testid="client-gst" />
        <div className="flex flex-col gap-1.5 sm:col-span-2">
          <label className="text-[11px] uppercase tracking-[0.16em] text-[#58635C]">Notes</label>
          <textarea data-testid="client-notes" rows={3} value={f.notes} onChange={(e) => set("notes")(e.target.value)}
            className="border border-[#E5E1D8] px-3.5 py-2.5 focus:border-[#B89874] outline-none resize-y" />
        </div>
      </div>
    </Modal>
  );
}

function MatterForm({ initial, courts, onClose, onSave }) {
  const [f, setF] = useState({ ...EMPTY_MATTER, ...initial });
  const [busy, setBusy] = useState(false);
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));
  const num = (v) => { const n = parseFloat(String(v).replace(/[^0-9.]/g, "")); return isNaN(n) ? 0 : n; };
  // typing a settlement auto-fills the write-off as original − settlement (still editable)
  const setSettlement = (v) => setF((s) => {
    const orig = num(s.amount), settle = num(v);
    const wo = orig > 0 && settle > 0 && orig > settle ? String(Math.round((orig - settle) * 100) / 100) : s.writeoff_amount;
    return { ...s, settlement_amount: v, writeoff_amount: wo };
  });
  const submit = async () => {
    if (!f.party_name.trim()) return toast.error("Party name is required");
    setBusy(true);
    try { await onSave(f); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <Modal testid="matter-form" title={initial.id ? "Edit matter" : "New matter"} subtitle="Party being recovered from" onClose={onClose}
      footer={<>
        <button onClick={onClose} className="px-5 py-3 text-sm font-semibold text-[#58635C] hover:text-[#0A0D0B]">Cancel</button>
        <button onClick={submit} disabled={busy} data-testid="matter-save" className="inline-flex items-center gap-2 bg-[#0A0D0B] text-white px-6 py-3 text-sm font-semibold uppercase tracking-[0.1em] hover:bg-[#233027] transition-colors disabled:opacity-60">{busy ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Save</button>
      </>}>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Party / debtor name" value={f.party_name} onChange={set("party_name")} testid="matter-name" full />
        <Field label="Party address" value={f.party_address} onChange={set("party_address")} testid="matter-address" full />
        <Field label="Party email (for sending letters)" value={f.party_email} onChange={set("party_email")} testid="matter-email" type="email" />
        <Field label="Original amount owed" value={f.amount} onChange={set("amount")} testid="matter-amount" />
        <div className="sm:col-span-2 mt-1 border-t border-[#F0EDE4] pt-4">
          <p className="text-[11px] uppercase tracking-[0.16em] text-[#8A6D2F] mb-1">Full &amp; final settlement (optional)</p>
          <p className="text-xs text-[#58635C] mb-3">If a reduced settlement is agreed, enter the negotiated amount and the amount written off. The balance owing will be based on the settlement figure.</p>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Negotiated settlement amount" value={f.settlement_amount} onChange={setSettlement} testid="matter-settlement" />
            <Field label="Amount written off by client" value={f.writeoff_amount} onChange={set("writeoff_amount")} testid="matter-writeoff" />
          </div>
        </div>
        <Field label="Your client's reference" value={f.client_ref} onChange={set("client_ref")} testid="matter-ref" />
        <Field label="Agreement / invoice description" value={f.agreement_desc} onChange={set("agreement_desc")} testid="matter-agreement" full />
        <Field label="Case / cause number" value={f.case_number} onChange={set("case_number")} testid="matter-case" />
        <Field label="Case year" value={f.case_year} onChange={set("case_year")} testid="matter-year" />
        <div className="flex flex-col gap-1.5 sm:col-span-2">
          <label className="text-[11px] uppercase tracking-[0.16em] text-[#58635C]">Court / registry (overrides client default)</label>
          <select data-testid="matter-court" value={f.court} onChange={(e) => set("court")(e.target.value)}
            className="border border-[#E5E1D8] px-3.5 py-2.5 focus:border-[#B89874] outline-none bg-white">
            <option value="">— use client default —</option>
            {courts.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
      </div>
    </Modal>
  );
}

function GenerateModal({ clientId, matter, onClose, onGenerated }) {
  const [docs, setDocs] = useState([]);
  const [slug, setSlug] = useState("");
  const [vars, setVars] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.get("/documents").then((r) => setDocs(r.data.documents.filter((d) => !d.signable)));
  }, []);

  const pickTemplate = async (s) => {
    setSlug(s); setVars(null); setResult(null);
    if (!s) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/documents/${s}/variables?client_id=${clientId}&matter_id=${matter.id}`);
      setVars(data.variables);
    } finally { setLoading(false); }
  };

  const setVar = (name, value) => setVars((vs) => vs.map((v) => (v.name === name ? { ...v, value } : v)));

  const generate = async () => {
    const missing = vars.filter((v) => !String(v.value).trim());
    if (missing.length) return toast.error(`Please complete: ${missing.map((m) => m.name).join(", ")}`);
    setBusy(true);
    try {
      const values = Object.fromEntries(vars.map((v) => [v.name, v.value]));
      const { data } = await api.post("/generate", { slug, client_id: clientId, matter_id: matter.id, values });
      toast.success("Document generated.");
      setResult(data);
      onGenerated();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <Modal testid="generate-modal" title="Generate letter" subtitle={`For ${matter.party_name}`} onClose={onClose}
      footer={result ? (
        <button onClick={onClose} className="px-6 py-3 text-sm font-semibold bg-[#0A0D0B] text-white uppercase tracking-[0.1em]">Done</button>
      ) : (<>
        <button onClick={onClose} className="px-5 py-3 text-sm font-semibold text-[#58635C] hover:text-[#0A0D0B]">Cancel</button>
        <button onClick={generate} disabled={busy || !vars} data-testid="generate-submit"
          className="inline-flex items-center gap-2 bg-[#0A0D0B] text-white px-6 py-3 text-sm font-semibold uppercase tracking-[0.1em] hover:bg-[#233027] transition-colors disabled:opacity-50">{busy ? <Loader2 size={15} className="animate-spin" /> : <Wand2 size={15} />} Generate</button>
      </>)}>
      {result ? (
        <div data-testid="generate-result" className="text-center py-4">
          <div className="text-sm text-[#233027]">Your <b>{result.title}</b> is ready — no editing required.</div>
          <div className="mt-3 font-mono-gg text-xl text-[#0A0D0B]">{result.reference}</div>
          <div className="mt-5 flex justify-center gap-3">
            <a href={`${API}/generated/${result.id}/pdf`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 bg-[#0A0D0B] text-white px-6 py-3 text-sm font-semibold uppercase tracking-[0.1em] hover:bg-[#233027] transition-colors"><Download size={15} /> PDF</a>
            <a href={`${API}/generated/${result.id}/docx`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 border border-[#E5E1D8] text-[#58635C] px-6 py-3 text-sm font-semibold hover:border-[#0A0D0B] hover:text-[#0A0D0B] transition-colors"><FileType size={15} /> Word</a>
          </div>
        </div>
      ) : (
        <div className="space-y-5">
          <div className="flex flex-col gap-1.5">
            <label className="text-[11px] uppercase tracking-[0.16em] text-[#58635C]">Choose a template</label>
            <select data-testid="generate-template" value={slug} onChange={(e) => pickTemplate(e.target.value)}
              className="border border-[#E5E1D8] px-3.5 py-2.5 focus:border-[#B89874] outline-none bg-white">
              <option value="">— select a document —</option>
              {docs.map((d) => <option key={d.slug} value={d.slug}>{d.title}</option>)}
            </select>
          </div>
          {loading && <Loader2 className="animate-spin text-[#B89874]" />}
          {vars && (
            vars.length === 0 ? (
              <p className="text-sm text-[#58635C]">This template has no fill-in fields — it will generate as-is.</p>
            ) : (
              <div className="grid sm:grid-cols-2 gap-4" data-testid="generate-fields">
                {vars.map((v) => {
                  const isLong = /work done|description|narrative|details/i.test(v.name);
                  return (
                    <div key={v.name} className={`flex flex-col gap-1.5 ${isLong ? "sm:col-span-2" : ""}`}>
                      <label className="text-[11px] uppercase tracking-[0.14em] text-[#58635C] flex items-center gap-2">
                        {v.name}
                        {v.prefilled && <span className="text-[9px] text-[#3F6B4E] bg-[#E7F0E9] px-1.5 py-0.5 rounded">auto</span>}
                      </label>
                      {isLong ? (
                        <textarea data-testid={`genvar-${v.name}`} rows={4} value={v.value || ""} onChange={(e) => setVar(v.name, e.target.value)}
                          className={`border px-3.5 py-2.5 outline-none focus:border-[#B89874] resize-y leading-relaxed ${v.value ? "border-[#E5E1D8]" : "border-[#E3B7B0] bg-[#FDF4F2]"}`} />
                      ) : (
                        <input data-testid={`genvar-${v.name}`} value={v.value || ""} onChange={(e) => setVar(v.name, e.target.value)}
                          className={`border px-3.5 py-2.5 outline-none focus:border-[#B89874] ${v.value ? "border-[#E5E1D8]" : "border-[#E3B7B0] bg-[#FDF4F2]"}`} />
                      )}
                    </div>
                  );
                })}
              </div>
            )
          )}
        </div>
      )}
    </Modal>
  );
}


function SettleModal({ matter, onClose, onDone, onRefresh }) {
  const parseAmt = (v) => parseFloat(String(v || "").replace(/[^0-9.]/g, "")) || 0;
  const [orig, setOrig] = useState(parseAmt(matter.amount));
  const [editOrig, setEditOrig] = useState(false);
  const [origInput, setOrigInput] = useState(String(parseAmt(matter.amount)));
  const [savingOrig, setSavingOrig] = useState(false);
  const [settlement, setSettlement] = useState("");
  const [due, setDue] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() + 14);
    return d.toISOString().slice(0, 10);
  });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [byInst, setByInst] = useState(false);
  const [count, setCount] = useState(4);
  const [freq, setFreq] = useState("monthly");
  const settleNum = parseFloat(String(settlement).replace(/[^0-9.]/g, "")) || 0;
  const writeoff = orig > 0 && settleNum > 0 && orig > settleNum ? Math.round((orig - settleNum) * 100) / 100 : 0;
  const dueLabel = due ? new Date(due).toLocaleDateString("en-NZ", { day: "2-digit", month: "long", year: "numeric" }) : "";

  const schedule = (() => {
    if (!byInst || settleNum <= 0 || count < 1) return [];
    const n = Math.min(24, Math.max(1, parseInt(count) || 1));
    const base = Math.floor((settleNum / n) * 100) / 100;
    const start = due ? new Date(due) : new Date();
    const out = [];
    for (let i = 0; i < n; i++) {
      const d = new Date(start);
      if (freq === "weekly") d.setDate(start.getDate() + 7 * i);
      else if (freq === "fortnightly") d.setDate(start.getDate() + 14 * i);
      else d.setMonth(start.getMonth() + i);
      const amt = i === n - 1 ? Math.round((settleNum - base * (n - 1)) * 100) / 100 : base;
      out.push({ due_date: d.toISOString().slice(0, 10), amount: amt });
    }
    return out;
  })();

  const saveOrig = async () => {
    const val = parseAmt(origInput);
    if (val <= 0) return toast.error("Enter a valid original amount.");
    setSavingOrig(true);
    try {
      await api.put(`/matters/${matter.id}`, {
        party_name: matter.party_name, party_address: matter.party_address || "", party_email: matter.party_email || "",
        amount: String(val), settlement_amount: matter.settlement_amount || "", writeoff_amount: matter.writeoff_amount || "",
        client_ref: matter.client_ref || "", agreement_desc: matter.agreement_desc || "", case_number: matter.case_number || "",
        case_year: matter.case_year || "", court: matter.court || "", status: matter.status || "Open", notes: matter.notes || "",
      });
      setOrig(val); setEditOrig(false); toast.success("Original amount updated."); onRefresh && onRefresh();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setSavingOrig(false); }
  };

  const submit = async () => {
    if (settleNum <= 0) return toast.error("Enter a settlement amount greater than zero.");
    setBusy(true);
    try {
      const { data } = await api.post(`/matters/${matter.id}/settle`, {
        settlement_amount: settleNum, writeoff_amount: writeoff,
        payment_due_date: due, instalments: byInst ? schedule : null,
      });
      setResult(data);
      toast.success("Settlement recorded and letter generated.");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const link = result ? `${window.location.origin}/settle/${result.settlement_token}` : "";

  return (
    <Modal testid="settle-modal" title="Full & final settlement" subtitle={`For ${matter.party_name}`} onClose={onClose}
      footer={result ? (
        <button onClick={onDone} className="px-6 py-3 text-sm font-semibold bg-[#0A0D0B] text-white uppercase tracking-[0.1em]">Done</button>
      ) : (<>
        <button onClick={onClose} className="px-5 py-3 text-sm font-semibold text-[#58635C] hover:text-[#0A0D0B]">Cancel</button>
        <button onClick={submit} disabled={busy} data-testid="settle-submit"
          className="inline-flex items-center gap-2 bg-[#8A6D2F] text-white px-6 py-3 text-sm font-semibold uppercase tracking-[0.1em] hover:bg-[#725923] transition-colors disabled:opacity-60">{busy ? <Loader2 size={15} className="animate-spin" /> : <Handshake size={15} />} Settle & generate</button>
      </>)}>
      {result ? (
        <div data-testid="settle-result" className="space-y-4">
          <p className="text-sm text-[#233027]">Settlement recorded. The <b>Full &amp; Final Settlement Confirmation</b> letter has been generated (Ref <span className="font-mono-gg">{result.generated.reference}</span>).</p>
          <div className="flex gap-3">
            <a href={`${API}/generated/${result.generated.id}/pdf`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 bg-[#0A0D0B] text-white px-5 py-2.5 text-sm font-semibold hover:bg-[#233027] transition-colors"><Download size={14} /> PDF</a>
            <a href={`${API}/generated/${result.generated.id}/docx`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 border border-[#E5E1D8] text-[#58635C] px-5 py-2.5 text-sm font-semibold hover:border-[#0A0D0B] hover:text-[#0A0D0B] transition-colors"><FileType size={14} /> Word</a>
          </div>
          <div className="border border-[#E5E1D8] bg-[#FBFAF5] p-4">
            <div className="text-[11px] uppercase tracking-[0.16em] text-[#58635C]">Online acceptance link for the debtor</div>
            <div className="mt-1.5 flex items-center gap-2">
              <input readOnly value={link} data-testid="settle-link" className="flex-1 bg-white border border-[#E5E1D8] px-3 py-2 text-sm font-mono-gg text-[#0A0D0B] truncate" />
              <button onClick={() => { navigator.clipboard?.writeText(link); toast.success("Link copied."); }} data-testid="settle-copy"
                className="inline-flex items-center gap-1.5 border border-[#0A0D0B] text-[#0A0D0B] px-3 py-2 text-sm font-semibold hover:bg-[#0A0D0B] hover:text-white transition-colors"><Copy size={13} /> Copy</button>
            </div>
            <p className="mt-2 text-xs text-[#58635C]">Send this to the debtor so they can review and sign to accept the settlement online.</p>
          </div>
        </div>
      ) : (
        <div className="space-y-5">
          {editOrig ? (
            <div className="flex items-center gap-2" data-testid="settle-orig-edit">
              <span className="text-sm text-[#58635C] shrink-0">Original debt:</span>
              <input autoFocus value={origInput} onChange={(e) => setOrigInput(e.target.value)} data-testid="settle-orig-input"
                className="w-36 border border-[#B89874] px-3 py-1.5 text-sm font-mono-gg focus:border-[#0A0D0B] outline-none" />
              <button onClick={saveOrig} disabled={savingOrig} data-testid="settle-orig-save"
                className="inline-flex items-center gap-1 text-xs font-semibold bg-[#0A0D0B] text-white px-3 py-1.5 hover:bg-[#233027] disabled:opacity-60">
                {savingOrig ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Save
              </button>
              <button onClick={() => { setEditOrig(false); setOrigInput(String(orig)); }} className="text-xs text-[#58635C] hover:text-[#0A0D0B] px-1"><X size={13} /></button>
            </div>
          ) : (
            <div className="flex items-center gap-2 text-sm text-[#58635C]" data-testid="settle-orig-display">
              Original debt: <b className="text-[#0A0D0B] font-mono-gg">{money(orig)}</b>
              <button onClick={() => { setOrigInput(String(orig)); setEditOrig(true); }} data-testid="settle-orig-edit-btn"
                title="Correct the original amount" className="text-[#B89874] hover:text-[#0A0D0B] transition-colors"><Pencil size={13} /></button>
            </div>
          )}
          <Field label="Negotiated settlement amount" value={settlement} onChange={setSettlement} testid="settle-amount" type="number" />

          <label className="flex items-center gap-3 cursor-pointer select-none" data-testid="settle-byinst-toggle">
            <button type="button" onClick={() => setByInst((v) => !v)} role="switch" aria-checked={byInst}
              className={`relative shrink-0 w-11 h-6 rounded-full transition-colors ${byInst ? "bg-[#8A6D2F]" : "bg-[#CFC9BC]"}`}>
              <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${byInst ? "translate-x-5" : ""}`} />
            </button>
            <span className="text-sm text-[#0A0D0B] font-semibold">Pay by instalments</span>
          </label>

          {byInst ? (
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-3">
                <div className="flex flex-col gap-1.5">
                  <label className="text-[11px] uppercase tracking-[0.16em] text-[#58635C]">Instalments</label>
                  <input type="number" min="1" max="24" value={count} onChange={(e) => setCount(e.target.value)} data-testid="settle-inst-count"
                    className="border border-[#E5E1D8] px-3 py-2.5 focus:border-[#B89874] outline-none bg-white" />
                </div>
                <div className="flex flex-col gap-1.5">
                  <label className="text-[11px] uppercase tracking-[0.16em] text-[#58635C]">Frequency</label>
                  <select value={freq} onChange={(e) => setFreq(e.target.value)} data-testid="settle-inst-freq"
                    className="border border-[#E5E1D8] px-3 py-2.5 focus:border-[#B89874] outline-none bg-white">
                    <option value="weekly">Weekly</option>
                    <option value="fortnightly">Fortnightly</option>
                    <option value="monthly">Monthly</option>
                  </select>
                </div>
                <div className="flex flex-col gap-1.5">
                  <label className="text-[11px] uppercase tracking-[0.16em] text-[#58635C]">First payment</label>
                  <input type="date" value={due} onChange={(e) => setDue(e.target.value)} data-testid="settle-inst-start"
                    className="border border-[#E5E1D8] px-3 py-2.5 focus:border-[#B89874] outline-none bg-white" />
                </div>
              </div>
              {schedule.length > 0 && (
                <div className="border border-[#E5E1D8] bg-[#FBFAF5]" data-testid="settle-schedule">
                  {schedule.map((s, i) => (
                    <div key={i} className="flex justify-between px-3.5 py-2 text-sm border-b border-[#F0EDE4] last:border-0">
                      <span className="text-[#58635C]">Instalment {i + 1} · {new Date(s.due_date).toLocaleDateString("en-NZ", { day: "2-digit", month: "short", year: "numeric" })}</span>
                      <span className="font-mono-gg text-[#0A0D0B]">{money(s.amount)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="flex flex-col gap-1.5">
              <label className="text-[11px] uppercase tracking-[0.16em] text-[#58635C]">Payment due by</label>
              <input type="date" value={due} onChange={(e) => setDue(e.target.value)} data-testid="settle-due"
                className="border border-[#E5E1D8] px-3.5 py-2.5 focus:border-[#B89874] outline-none bg-white" />
            </div>
          )}
          {settleNum > 0 && (
            <div className="border border-[#E8D9B0] bg-[#FBF2DE] p-4 text-sm text-[#8A6D2F]" data-testid="settle-preview">
              Debtor pays <b>{money(settleNum)}</b> in full &amp; final{byInst && schedule.length ? ` over ${schedule.length} ${freq} instalment${schedule.length === 1 ? "" : "s"}` : ""} — <b>{money(writeoff)}</b> written off. A confirmation letter with an online acceptance link will be created.
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}



function BulkModal({ clientId, matters, onClose, onDone }) {
  const [docs, setDocs] = useState([]);
  const [slug, setSlug] = useState("");
  const [selected, setSelected] = useState(matters.map((m) => m.id));
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState(null);

  useEffect(() => { api.get("/documents").then((r) => setDocs(r.data.documents.filter((d) => !d.signable))); }, []);
  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const run = async () => {
    if (!slug) return toast.error("Choose a template");
    if (!selected.length) return toast.error("Select at least one matter");
    setBusy(true);
    try {
      const { data } = await api.post("/generate/bulk", { slug, client_id: clientId, matter_ids: selected });
      setResults(data.results);
      const ok = data.results.filter((r) => r.ok).length;
      toast.success(`${ok} of ${data.results.length} generated.`);
      onDone();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  return (
    <Modal testid="bulk-modal" title="Bulk letters" subtitle="Generate for several matters" onClose={onClose}
      footer={results ? (
        <button onClick={onClose} className="px-6 py-3 text-sm font-semibold bg-[#0A0D0B] text-white uppercase tracking-[0.1em]">Done</button>
      ) : (<>
        <button onClick={onClose} className="px-5 py-3 text-sm font-semibold text-[#58635C] hover:text-[#0A0D0B]">Cancel</button>
        <button onClick={run} disabled={busy} data-testid="bulk-run" className="inline-flex items-center gap-2 bg-[#0A0D0B] text-white px-6 py-3 text-sm font-semibold uppercase tracking-[0.1em] hover:bg-[#233027] transition-colors disabled:opacity-60">{busy ? <Loader2 size={15} className="animate-spin" /> : <Layers size={15} />} Generate {selected.length}</button>
      </>)}>
      {results ? (
        <div data-testid="bulk-results" className="space-y-2">
          {results.map((r) => (
            <div key={r.matter_id} className="flex items-center justify-between gap-3 border border-[#F0EDE4] bg-[#FBFAF5] px-4 py-2.5">
              <span className="text-sm text-[#0A0D0B]">{r.party_name}</span>
              {r.ok ? <span className="text-xs font-mono-gg text-[#3F6B4E]">{r.reference}</span>
                : <span className="text-xs text-[#B0463A]">Missing: {(r.missing || [r.error]).join(", ")}</span>}
            </div>
          ))}
          <p className="text-xs text-[#58635C] pt-1">Skipped matters were missing fields that couldn't be auto-filled — add them on the matter and try again, or generate individually.</p>
        </div>
      ) : (
        <div className="space-y-5">
          <div className="flex flex-col gap-1.5">
            <label className="text-[11px] uppercase tracking-[0.16em] text-[#58635C]">Template</label>
            <select data-testid="bulk-template" value={slug} onChange={(e) => setSlug(e.target.value)}
              className="border border-[#E5E1D8] px-3.5 py-2.5 focus:border-[#B89874] outline-none bg-white">
              <option value="">— select a document —</option>
              {docs.map((d) => <option key={d.slug} value={d.slug}>{d.title}</option>)}
            </select>
            <span className="text-xs text-[#58635C]">Fields are auto-filled from each matter. Matters missing required fields will be skipped and listed.</span>
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-[0.16em] text-[#58635C]">Matters</label>
            <div className="mt-2 space-y-1.5 max-h-64 overflow-y-auto">
              {matters.map((m) => (
                <label key={m.id} className="flex items-center gap-3 border border-[#E5E1D8] px-3.5 py-2.5 cursor-pointer hover:bg-[#FBFAF5]">
                  <input type="checkbox" checked={selected.includes(m.id)} onChange={() => toggle(m.id)} data-testid={`bulk-check-${m.id}`} />
                  <span className="text-sm text-[#0A0D0B]">{m.party_name}</span>
                  {m.amount && <span className="text-xs text-[#B89874] ml-auto font-mono-gg">{m.amount}</span>}
                </label>
              ))}
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
}

function EmailModal({ gen, onClose, onSent }) {
  const [to, setTo] = useState("debtor");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/generated/${gen.id}/email`, { to, email, message });
      toast.success(`Sent to ${data.recipient}.`);
      onSent();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const opt = (v, label) => (
    <label className={`flex items-center gap-2 border px-3.5 py-2.5 cursor-pointer text-sm ${to === v ? "border-[#0A0D0B] bg-[#F5F3EC]" : "border-[#E5E1D8]"}`}>
      <input type="radio" name="emailto" checked={to === v} onChange={() => setTo(v)} data-testid={`emailto-${v}`} /> {label}
    </label>
  );

  return (
    <Modal testid="email-modal" title="Email document" subtitle={`${gen.title} · ${gen.reference}`} onClose={onClose}
      footer={<>
        <button onClick={onClose} className="px-5 py-3 text-sm font-semibold text-[#58635C] hover:text-[#0A0D0B]">Cancel</button>
        <button onClick={send} disabled={busy} data-testid="email-send" className="inline-flex items-center gap-2 bg-[#0A0D0B] text-white px-6 py-3 text-sm font-semibold uppercase tracking-[0.1em] hover:bg-[#233027] transition-colors disabled:opacity-60">{busy ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />} Send</button>
      </>}>
      <div className="space-y-4">
        <div className="grid grid-cols-3 gap-2">{opt("debtor", "Debtor")}{opt("client", "Client")}{opt("custom", "Custom")}</div>
        {to === "custom" && (
          <div className="flex flex-col gap-1.5">
            <label className="text-[11px] uppercase tracking-[0.16em] text-[#58635C]">Email address</label>
            <input data-testid="email-custom" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
              className="border border-[#E5E1D8] px-3.5 py-2.5 focus:border-[#B89874] outline-none" />
          </div>
        )}
        <div className="flex flex-col gap-1.5">
          <label className="text-[11px] uppercase tracking-[0.16em] text-[#58635C]">Message (optional)</label>
          <textarea data-testid="email-message" rows={4} value={message} onChange={(e) => setMessage(e.target.value)}
            className="border border-[#E5E1D8] px-3.5 py-2.5 focus:border-[#B89874] outline-none resize-y text-sm" />
        </div>
        <p className="text-xs text-[#58635C]">The PDF is attached automatically and a copy is recorded on the matter timeline.</p>
      </div>
    </Modal>
  );
}

function MatterFiles({ matterId }) {
  const [atts, setAtts] = useState(null);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);
  const load = () => api.get(`/matters/${matterId}/attachments`).then((r) => setAtts(r.data.attachments));
  useEffect(() => { load(); }, [matterId]);

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/matters/${matterId}/attachments`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("File attached.");
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const del = async (id) => {
    if (!window.confirm("Remove this file?")) return;
    await api.delete(`/attachments/${id}`); toast.success("File removed."); load();
  };

  return (
    <div className="mt-4 border-t border-[#F0EDE4] pt-3" data-testid={`files-${matterId}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-[11px] uppercase tracking-[0.16em] text-[#58635C]">Attachments</div>
        <input ref={inputRef} type="file" className="hidden" data-testid={`file-input-${matterId}`}
          onChange={(e) => { upload(e.target.files[0]); e.target.value = ""; }} />
        <button onClick={() => inputRef.current?.click()} disabled={busy} data-testid={`upload-file-${matterId}`}
          className="inline-flex items-center gap-2 text-xs font-semibold border border-[#0A0D0B] text-[#0A0D0B] px-3 py-1.5 hover:bg-[#0A0D0B] hover:text-white transition-colors disabled:opacity-60">
          {busy ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />} Upload
        </button>
      </div>
      {!atts ? <Loader2 size={15} className="animate-spin text-[#B89874]" /> : atts.length === 0 ? (
        <p className="text-sm text-[#58635C]">No files yet. Upload invoices, contracts or evidence to keep the file complete.</p>
      ) : (
        <ul className="space-y-1.5">
          {atts.map((a) => (
            <li key={a.id} className="flex items-center gap-3 bg-[#FBFAF5] border border-[#F0EDE4] px-3 py-2">
              <Paperclip size={13} className="text-[#B89874] shrink-0" />
              <a href={`${API}/attachments/${a.id}/download`} target="_blank" rel="noreferrer" className="text-sm text-[#0A0D0B] hover:underline truncate">{a.filename}</a>
              <span className="text-[11px] text-[#B89874] ml-auto shrink-0">{Math.round(a.size / 1024)} KB</span>
              <button onClick={() => del(a.id)} data-testid={`delete-file-${a.id}`} className="text-[#58635C] hover:text-red-600 shrink-0"><Trash2 size={13} /></button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function MatterPayments({ matterId, onChange }) {
  const [data, setData] = useState(null);
  const [amount, setAmount] = useState("");
  const [date, setDate] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [receiptFor, setReceiptFor] = useState(null);
  const load = () => api.get(`/matters/${matterId}/payments`).then((r) => setData(r.data));
  useEffect(() => { load(); }, [matterId]);

  const add = async () => {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) return toast.error("Enter a payment amount greater than zero");
    setBusy(true);
    try {
      await api.post(`/matters/${matterId}/payments`, { amount: amt, date: date || undefined, note });
      setAmount(""); setNote(""); setDate("");
      toast.success("Payment recorded.");
      load(); onChange?.();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const del = async (id) => {
    if (!window.confirm("Remove this payment?")) return;
    await api.delete(`/payments/${id}`); toast.success("Payment removed."); load(); onChange?.();
  };

  return (
    <div className="mt-4 border-t border-[#F0EDE4] pt-3" data-testid={`payments-${matterId}`}>
      <div className="text-[11px] uppercase tracking-[0.16em] text-[#58635C] mb-2">Part-payments</div>
      {data && (
        <div className="grid grid-cols-3 gap-2 mb-3">
          <Summary label="Original" value={money(data.original)} />
          <Summary label="Paid" value={money(data.paid)} accent="#3F6B4E" />
          <Summary label="Outstanding" value={money(data.outstanding)} accent={data.outstanding > 0 ? "#8A6D2F" : "#3F6B4E"} testid={`payments-outstanding-${matterId}`} />
        </div>
      )}
      <div className="flex flex-wrap gap-2 mb-3">
        <input value={amount} onChange={(e) => setAmount(e.target.value)} data-testid={`payment-amount-${matterId}`}
          type="number" min="0" step="0.01" placeholder="Amount (e.g. 500)"
          className="w-32 border border-[#E5E1D8] px-3 py-2 text-sm focus:border-[#B89874] outline-none font-mono-gg" />
        <input value={date} onChange={(e) => setDate(e.target.value)} data-testid={`payment-date-${matterId}`}
          type="date" className="border border-[#E5E1D8] px-3 py-2 text-sm focus:border-[#B89874] outline-none" />
        <input value={note} onChange={(e) => setNote(e.target.value)} data-testid={`payment-note-${matterId}`}
          onKeyDown={(e) => e.key === "Enter" && add()} placeholder="Reference / note (optional)"
          className="flex-1 min-w-[140px] border border-[#E5E1D8] px-3 py-2 text-sm focus:border-[#B89874] outline-none" />
        <button onClick={add} disabled={busy} data-testid={`payment-add-${matterId}`}
          className="inline-flex items-center gap-1.5 text-xs font-semibold border border-[#3F6B4E] text-[#3F6B4E] px-3 py-2 hover:bg-[#3F6B4E] hover:text-white transition-colors disabled:opacity-60">
          {busy ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />} Record
        </button>
      </div>
      {!data ? <Loader2 size={15} className="animate-spin text-[#B89874]" /> : data.payments.length === 0 ? (
        <p className="text-sm text-[#58635C]">No payments recorded yet.</p>
      ) : (
        <ul className="space-y-1.5">
          {data.payments.map((p) => (
            <li key={p.id} className="flex items-center gap-3 bg-[#FBFAF5] border border-[#F0EDE4] px-3 py-2">
              <HandCoins size={13} className="text-[#3F6B4E] shrink-0" />
              <span className="text-sm font-mono-gg text-[#0A0D0B]">{money(p.amount)}</span>
              <span className="text-xs text-[#58635C]">{p.date}{p.note && ` · ${p.note}`}</span>
              <div className="ml-auto flex items-center gap-1 shrink-0">
                <a href={`${API}/payments/${p.id}/receipt`} target="_blank" rel="noreferrer" data-testid={`receipt-download-${p.id}`}
                  title="Download receipt" className="p-1.5 text-[#58635C] hover:text-[#0A0D0B]"><Receipt size={14} /></a>
                <button onClick={() => setReceiptFor(p)} data-testid={`receipt-email-${p.id}`}
                  title="Email receipt" className="p-1.5 text-[#58635C] hover:text-[#0A0D0B]"><Send size={13} /></button>
                <button onClick={() => del(p.id)} data-testid={`delete-payment-${p.id}`} className="p-1.5 text-[#58635C] hover:text-red-600"><Trash2 size={13} /></button>
              </div>
            </li>
          ))}
        </ul>
      )}
      <PaymentPlan matterId={matterId} onChange={() => { load(); onChange?.(); }} />
      {receiptFor && <ReceiptEmailModal payment={receiptFor} onClose={() => setReceiptFor(null)} />}
    </div>
  );
}

const PLAN_STATUS_STYLE = {
  paid: "text-[#3F6B4E] border-[#C6DDCC] bg-[#E7F0E9]",
  missed: "text-red-700 border-red-300 bg-red-50",
  upcoming: "text-[#58635C] border-[#E5E1D8] bg-[#F5F3EC]",
};

function PaymentPlan({ matterId, onChange }) {
  const [plan, setPlan] = useState(null);
  const [editing, setEditing] = useState(false);
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [remindingId, setRemindingId] = useState(null);
  const load = () => api.get(`/matters/${matterId}/plan`).then((r) => setPlan(r.data.instalments));
  useEffect(() => { load(); }, [matterId]);

  const startEdit = () => {
    setRows((plan || []).map((i) => ({ _k: i.id || crypto.randomUUID(), due_date: i.due_date, amount: String(i.amount) })));
    setEditing(true);
  };
  const addRow = () => setRows((r) => [...r, { _k: crypto.randomUUID(), due_date: "", amount: "" }]);
  const setRow = (idx, k, v) => setRows((r) => r.map((row, i) => (i === idx ? { ...row, [k]: v } : row)));
  const removeRow = (idx) => setRows((r) => r.filter((_, i) => i !== idx));
  const save = async () => {
    const instalments = rows
      .filter((r) => r.due_date && parseFloat(r.amount) > 0)
      .map((r) => ({ due_date: r.due_date, amount: parseFloat(r.amount) }));
    setBusy(true);
    try {
      const { data } = await api.put(`/matters/${matterId}/plan`, { instalments });
      setPlan(data.instalments); setEditing(false);
      toast.success("Payment plan saved."); onChange?.();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const remind = async (id) => {
    setRemindingId(id);
    try {
      const { data } = await api.post(`/matters/${matterId}/plan/${id}/remind`, { message: "" });
      toast.success(`Reminder emailed to ${data.recipient}.`);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setRemindingId(null); }
  };

  return (
    <div className="mt-4 border-t border-dashed border-[#E5E1D8] pt-3" data-testid={`plan-${matterId}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-[11px] uppercase tracking-[0.16em] text-[#58635C]">Payment plan</div>
        {!editing && (
          <button onClick={startEdit} data-testid={`plan-edit-${matterId}`}
            className="inline-flex items-center gap-1.5 text-xs font-semibold border border-[#0A0D0B] text-[#0A0D0B] px-3 py-1.5 hover:bg-[#0A0D0B] hover:text-white transition-colors">
            <CalendarClock size={13} /> {plan && plan.length ? "Edit plan" : "Set plan"}
          </button>
        )}
      </div>
      {editing ? (
        <div className="space-y-2" data-testid={`plan-editor-${matterId}`}>
          {rows.map((r, i) => (
            <div key={r._k} className="flex flex-wrap items-center gap-2">
              <input type="date" value={r.due_date} onChange={(e) => setRow(i, "due_date", e.target.value)} data-testid={`plan-date-${matterId}-${i}`}
                className="border border-[#E5E1D8] px-3 py-2 text-sm focus:border-[#B89874] outline-none" />
              <input type="number" min="0" step="0.01" value={r.amount} onChange={(e) => setRow(i, "amount", e.target.value)} placeholder="Amount" data-testid={`plan-amount-${matterId}-${i}`}
                className="w-32 border border-[#E5E1D8] px-3 py-2 text-sm focus:border-[#B89874] outline-none font-mono-gg" />
              <button onClick={() => removeRow(i)} className="p-2 text-[#58635C] hover:text-red-600"><X size={14} /></button>
            </div>
          ))}
          <div className="flex items-center gap-2 pt-1">
            <button onClick={addRow} data-testid={`plan-add-row-${matterId}`} className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#B89874] hover:text-[#0A0D0B]"><Plus size={13} /> Add instalment</button>
            <div className="ml-auto flex gap-2">
              <button onClick={() => setEditing(false)} className="px-3 py-1.5 text-xs font-semibold text-[#58635C] hover:text-[#0A0D0B]">Cancel</button>
              <button onClick={save} disabled={busy} data-testid={`plan-save-${matterId}`} className="inline-flex items-center gap-1.5 text-xs font-semibold bg-[#0A0D0B] text-white px-3 py-1.5 hover:bg-[#233027] transition-colors disabled:opacity-60">{busy ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Save plan</button>
            </div>
          </div>
        </div>
      ) : !plan ? <Loader2 size={15} className="animate-spin text-[#B89874]" /> : plan.length === 0 ? (
        <p className="text-sm text-[#58635C]">No plan set. Add an agreed instalment schedule to track missed payments.</p>
      ) : (
        <ul className="space-y-1.5">
          {plan.map((i) => (
            <li key={i.id} data-testid={`plan-instalment-${i.id}`} className="flex items-center gap-3 border border-[#F0EDE4] bg-white px-3 py-2">
              <span className="text-sm text-[#0A0D0B]">{i.due_date}</span>
              <span className="text-sm font-mono-gg text-[#233027]">{money(i.amount)}</span>
              <span className={`text-[10px] uppercase tracking-wide px-2 py-0.5 border ${PLAN_STATUS_STYLE[i.status]}`}>
                {i.status === "paid" ? "Paid" : i.status === "missed" ? "Missed" : "Upcoming"}
              </span>
              {i.status !== "paid" && (
                <button onClick={() => remind(i.id)} disabled={remindingId === i.id} data-testid={`plan-remind-${i.id}`}
                  title="Email the debtor a reminder" className="ml-auto inline-flex items-center gap-1 text-xs font-semibold text-[#8A6D2F] hover:text-[#0A0D0B] disabled:opacity-60">
                  {remindingId === i.id ? <Loader2 size={12} className="animate-spin" /> : <Bell size={12} />} Remind
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ReceiptEmailModal({ payment, onClose }) {
  const [to, setTo] = useState("client");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/payments/${payment.id}/email`, { to, email, message });
      toast.success(`Receipt sent to ${data.recipient}.`); onClose();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const opt = (v, label) => (
    <label className={`flex items-center gap-2 border px-3.5 py-2.5 cursor-pointer text-sm ${to === v ? "border-[#0A0D0B] bg-[#F5F3EC]" : "border-[#E5E1D8]"}`}>
      <input type="radio" name="receiptto" checked={to === v} onChange={() => setTo(v)} data-testid={`receiptto-${v}`} /> {label}
    </label>
  );
  return (
    <Modal testid="receipt-email-modal" title="Email receipt" subtitle={`Payment ${money(payment.amount)}`} onClose={onClose}
      footer={<>
        <button onClick={onClose} className="px-5 py-3 text-sm font-semibold text-[#58635C] hover:text-[#0A0D0B]">Cancel</button>
        <button onClick={send} disabled={busy} data-testid="receipt-send" className="inline-flex items-center gap-2 bg-[#0A0D0B] text-white px-6 py-3 text-sm font-semibold uppercase tracking-[0.1em] hover:bg-[#233027] transition-colors disabled:opacity-60">{busy ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />} Send</button>
      </>}>
      <div className="space-y-4">
        <div className="grid grid-cols-3 gap-2">{opt("debtor", "Debtor")}{opt("client", "Client")}{opt("custom", "Custom")}</div>
        {to === "custom" && (
          <div className="flex flex-col gap-1.5">
            <label className="text-[11px] uppercase tracking-[0.16em] text-[#58635C]">Email address</label>
            <input data-testid="receipt-custom" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
              className="border border-[#E5E1D8] px-3.5 py-2.5 focus:border-[#B89874] outline-none" />
          </div>
        )}
        <div className="flex flex-col gap-1.5">
          <label className="text-[11px] uppercase tracking-[0.16em] text-[#58635C]">Message (optional)</label>
          <textarea data-testid="receipt-message" rows={3} value={message} onChange={(e) => setMessage(e.target.value)}
            className="border border-[#E5E1D8] px-3.5 py-2.5 focus:border-[#B89874] outline-none resize-y text-sm" />
        </div>
        <p className="text-xs text-[#58635C]">A branded PDF receipt is attached automatically.</p>
      </div>
    </Modal>
  );
}

function Summary({ label, value, accent = "#0A0D0B", testid }) {
  return (
    <div className="border border-[#F0EDE4] bg-[#FBFAF5] px-3 py-2" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.14em] text-[#58635C]">{label}</div>
      <div className="text-sm font-mono-gg mt-0.5" style={{ color: accent }}>{value}</div>
    </div>
  );
}

function PortalModal({ client, onClose, onSaved }) {  const [password, setPassword] = useState(client.portal_password_plain || "");
  const [enabled, setEnabled] = useState(!!client.portal_enabled);
  const [busy, setBusy] = useState(false);
  const genPw = () => {
    const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789";
    setPassword(Array.from({ length: 10 }, () => chars[Math.floor(Math.random() * chars.length)]).join(""));
  };
  const copy = () => { navigator.clipboard?.writeText(password); toast.success("Password copied."); };
  const save = async () => {
    setBusy(true);
    try {
      await api.post(`/clients/${client.id}/portal`, { password: password || undefined, enabled });
      toast.success("Portal access updated.");
      onSaved();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <Modal testid="portal-modal" title="Client portal access" subtitle={client.name} onClose={onClose}
      footer={<>
        <button onClick={onClose} className="px-5 py-3 text-sm font-semibold text-[#58635C] hover:text-[#0A0D0B]">Cancel</button>
        <button onClick={save} disabled={busy} data-testid="portal-save" className="inline-flex items-center gap-2 bg-[#0A0D0B] text-white px-6 py-3 text-sm font-semibold uppercase tracking-[0.1em] hover:bg-[#233027] transition-colors disabled:opacity-60">{busy ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Save</button>
      </>}>
      <div className="space-y-4">
        <p className="text-sm text-[#58635C]">The client signs in at <span className="font-mono-gg text-[#0A0D0B]">/client</span> with their email (<b>{client.email || "— add an email first —"}</b>) and the password below, to view their matters and documents.</p>
        <label className="flex items-center gap-2 text-sm text-[#0A0D0B]">
          <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} data-testid="portal-enabled" /> Enable portal access
        </label>
        <div className="flex flex-col gap-1.5">
          <label className="text-[11px] uppercase tracking-[0.16em] text-[#58635C]">Password (visible — share with the client)</label>
          <div className="flex gap-2">
            <input data-testid="portal-password" type="text" value={password} onChange={(e) => setPassword(e.target.value)}
              placeholder="At least 6 characters" className="flex-1 border border-[#E5E1D8] px-3.5 py-2.5 focus:border-[#B89874] outline-none font-mono-gg" />
            <button onClick={genPw} data-testid="portal-generate" className="px-3 py-2.5 border border-[#E5E1D8] text-[#58635C] text-sm font-semibold hover:border-[#0A0D0B] hover:text-[#0A0D0B] transition-colors">Generate</button>
            {password && <button onClick={copy} data-testid="portal-copy" className="px-3 py-2.5 border border-[#E5E1D8] text-[#58635C] text-sm font-semibold hover:border-[#0A0D0B] hover:text-[#0A0D0B] transition-colors">Copy</button>}
          </div>
          {!password && <span className="text-xs text-[#58635C]">Leave blank when enabling and a secure password will be generated automatically.</span>}
        </div>
      </div>
    </Modal>
  );
}

function ImportModal({ clientId, onClose, onImported }) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const inputRef = useRef(null);

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/clients/${clientId}/matters/import`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
      toast.success(`${data.created} matters imported.`);
      onImported();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  return (
    <Modal testid="import-modal" title="Import matters from CSV" subtitle="Bulk add debtors" onClose={onClose}
      footer={<button onClick={onClose} className="px-6 py-3 text-sm font-semibold bg-[#0A0D0B] text-white uppercase tracking-[0.1em]">Close</button>}>
      <div className="space-y-4">
        <p className="text-sm text-[#58635C]">Upload a CSV with a header row. Recognised columns: <span className="font-mono-gg text-[#0A0D0B]">party_name, address, email, amount, reference, agreement, case_number, year, court</span>. Only <b>party_name</b> is required.</p>
        <a href={`data:text/csv;charset=utf-8,${encodeURIComponent("party_name,address,email,amount,reference,agreement,case_number,year,court\n")}`}
          download="grafton-matters-template.csv" className="inline-flex items-center gap-2 text-sm font-semibold text-[#B89874] hover:text-[#0A0D0B]"><Download size={14} /> Download template</a>
        <input ref={inputRef} type="file" accept=".csv,text/csv" className="hidden" data-testid="import-file"
          onChange={(e) => { upload(e.target.files[0]); e.target.value = ""; }} />
        <button onClick={() => inputRef.current?.click()} disabled={busy} data-testid="import-upload"
          className="inline-flex items-center gap-2 bg-[#0A0D0B] text-white px-6 py-3 text-sm font-semibold uppercase tracking-[0.1em] hover:bg-[#233027] transition-colors disabled:opacity-60">
          {busy ? <Loader2 size={15} className="animate-spin" /> : <FileUp size={15} />} Choose CSV file
        </button>
        {result && (
          <div data-testid="import-result" className="border border-[#E5E1D8] p-4 bg-[#FBFAF5]">
            <div className="text-sm text-[#0A0D0B] font-medium">{result.created} matters created.</div>
            {result.errors?.length > 0 && (
              <ul className="mt-2 text-xs text-[#B0463A] space-y-0.5">{result.errors.map((e, i) => <li key={i}>{e}</li>)}</ul>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
}

